import { supabase } from "~/integrations/supabase/client";
import type {
  AccountClassificationGroup,
  ClassificationGroupFilters,
  ClassificationGroupFormData,
} from "~/types/contabilidad";

export interface ClassificationGroupImportResult {
  created: number;
  skipped: number;
  errors: Array<{ code: string; error: string }>;
}

export const classificationGroupService = {
  async getAll(
    filters?: ClassificationGroupFilters
  ): Promise<AccountClassificationGroup[]> {
    let query = supabase
      .from("account_classification_group")
      .select("*, company:company_id(*)")
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query.order("orden", { ascending: true });

    if (error) throw error;
    return data as AccountClassificationGroup[];
  },

  async getByCompany(companyId: string): Promise<AccountClassificationGroup[]> {
    const { data, error } = await supabase
      .from("account_classification_group")
      .select("*")
      .eq("company_id", companyId)
      .eq("is_deleted", false)
      .order("orden", { ascending: true });

    if (error) throw error;
    return data as AccountClassificationGroup[];
  },

  async getById(id: string): Promise<AccountClassificationGroup> {
    const { data, error } = await supabase
      .from("account_classification_group")
      .select("*, company:company_id(*)")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as AccountClassificationGroup;
  },

  async create(
    formData: ClassificationGroupFormData
  ): Promise<AccountClassificationGroup> {
    // Get max orden for this company if not provided
    let orden = formData.orden;
    if (orden === undefined) {
      const { data: maxOrden } = await supabase
        .from("account_classification_group")
        .select("orden")
        .eq("company_id", formData.company_id)
        .eq("is_deleted", false)
        .order("orden", { ascending: false })
        .limit(1)
        .single();

      orden = (maxOrden?.orden ?? 0) + 1;
    }

    const { data, error } = await supabase
      .from("account_classification_group")
      .insert({
        ...formData,
        orden,
      })
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountClassificationGroup;
  },

  async update(
    id: string,
    formData: Partial<ClassificationGroupFormData>
  ): Promise<AccountClassificationGroup> {
    const { data, error } = await supabase
      .from("account_classification_group")
      .update(formData)
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountClassificationGroup;
  },

  async delete(id: string): Promise<void> {
    // Soft delete
    const { error } = await supabase
      .from("account_classification_group")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) throw error;
  },

  /**
   * Create multiple classification groups in batch
   * Skips groups whose codes already exist
   */
  async createBatch(
    companyId: string,
    groups: Omit<ClassificationGroupFormData, "company_id">[]
  ): Promise<ClassificationGroupImportResult> {
    const result: ClassificationGroupImportResult = {
      created: 0,
      skipped: 0,
      errors: [],
    };

    if (groups.length === 0) {
      return result;
    }

    // Get existing codes for this company
    const { data: existingGroups, error: fetchError } = await supabase
      .from("account_classification_group")
      .select("code")
      .eq("company_id", companyId)
      .eq("is_deleted", false);

    if (fetchError) {
      throw fetchError;
    }

    const existingCodes = new Set(
      (existingGroups || []).map((g) => g.code.toLowerCase())
    );

    // Filter out groups that already exist
    const groupsToCreate = groups.filter((g) => {
      if (existingCodes.has(g.code.toLowerCase())) {
        result.skipped++;
        return false;
      }
      return true;
    });

    if (groupsToCreate.length === 0) {
      return result;
    }

    // Get max orden
    const { data: maxOrdenData } = await supabase
      .from("account_classification_group")
      .select("orden")
      .eq("company_id", companyId)
      .eq("is_deleted", false)
      .order("orden", { ascending: false })
      .limit(1)
      .single();

    let currentOrden = (maxOrdenData?.orden ?? 0) + 1;

    // Process in batches of 50
    const BATCH_SIZE = 50;
    for (let i = 0; i < groupsToCreate.length; i += BATCH_SIZE) {
      const batch = groupsToCreate.slice(i, i + BATCH_SIZE);

      const insertData = batch.map((g, idx) => ({
        company_id: companyId,
        code: g.code,
        name: g.name,
        orden: g.orden ?? currentOrden + idx,
      }));

      currentOrden += batch.length;

      const { data, error } = await supabase
        .from("account_classification_group")
        .insert(insertData)
        .select();

      if (error) {
        // If batch fails, try individual inserts
        for (const g of batch) {
          const { error: singleError } = await supabase
            .from("account_classification_group")
            .insert({
              company_id: companyId,
              code: g.code,
              name: g.name,
              orden: g.orden ?? currentOrden++,
            })
            .select()
            .single();

          if (singleError) {
            result.errors.push({
              code: g.code,
              error: singleError.message,
            });
          } else {
            result.created++;
          }
        }
      } else {
        result.created += data?.length ?? 0;
      }
    }

    return result;
  },
};
